<template>
  <div id="app">
    <ConsentPreferencesButton
      v-if="consentCenterEnabled"
      @click="showConsentPreferences"
    />
    <router-view />

    <Toast />
  </div>
</template>

<script lang="ts">
  import Toast from "atoms/notification/toast.vue";
  import ConsentPreferencesButton from "atoms/consent/consent-preferences-button.vue";

  import { Times, RouteNames } from "utils/constants";
  import { defineComponent } from "vue";
  import throttle from "lodash/throttle";
  import { useStripe } from "@/composables/useStripe";
  import { getActiveUserManager } from "./store/userManagers";
  import { NavigationFailureType, isNavigationFailure } from "vue-router";
  import { Member } from "./api/members.api";
  import { useToastStore } from "./store/toast";
  import { useRootStore } from "./store/root";
  import { supportedLocales } from "src/globals";
  import { User } from "oidc-client-ts";
  import * as Sentry from "@sentry/vue";
  import { useFeatureFlags } from "@/composables/feature-flags/useFeatureFlags";
  import {
    initOP,
    initTrackingFlags,
    identifyUser,
    resetTracking
  } from "@/utils/tracking/trackingUtility";

  const logoutMessage = "You were logged out due to inactivity";

  export default defineComponent({
    name: "App",

    components: {
      Toast,
      ConsentPreferencesButton
    },

    setup() {
      const { load } = useStripe();
      const rootStore = useRootStore();
      const toastStore = useToastStore();
      const { oursPrivacyId, oursEnabled, consentCenterEnabled } = useFeatureFlags();

      initTrackingFlags({ oursPrivacyId, oursEnabled });

      return {
        loadStripe: load,
        toastStore,
        rootStore,
        oursPrivacyId,
        oursEnabled,
        consentCenterEnabled
      };
    },

    data() {
      return {
        lastActionTime: new Date().getTime(),
        idleTimeoutHandle: undefined as
          | ReturnType<typeof setInterval>
          | undefined,
        showingToast: false
      };
    },

    head() {
      return {
        link: [this.canonicalLink, ...this.hrefLangLinks]
      };
    },

    computed: {
      authenticated(): boolean {
        return this.rootStore.authenticated;
      },
      consentCenterEnabled(): boolean {
        return this.consentCenterEnabled;
      },
      member(): Member | undefined {
        return this.rootStore.member;
      },
      canonicalLink() {
        return {
          rel: "canonical",
          href:
            location.protocol +
            "//" +
            location.host +
            this.$router.options.history.base +
            this.$route.path
        };
      },
      hrefLangLinks() {
        return supportedLocales.map(locale => ({
          rel: "alternate",
          hreflang: locale,
          href:
            location.protocol +
            "//" +
            location.host +
            (locale === "en-us" ? "" : `/${locale}`) +
            this.$route.path
        }));
      }
    },

    watch: {
      "authenticated": {
        async handler(newValue) {
          // If we become unauthenticated and the current route requires authentication
          // then we need to navigate back to home
          // Use $route.matched rather than $route.meta directly so children inherited meta also get this redirect applied
          if (!newValue && this.$route.matched.some(m => m.meta.requiresAuth)) {
            this.$router.push({ name: RouteNames.SignIn });
          }

          if (newValue) {
            this.onUserActivity();
            try {
              if (this.$route.name === RouteNames.SignIn) {
                await this.$router.isReady();
                await this.$router.push({ name: RouteNames.Home });
              }

              const member = this.rootStore.member;
              // Ours Privacy identify, if enabled
              identifyUser(member);
            } catch (err) {
              // vue-router throws an error in any case where a middleware function
              // redirects the request, even if it is the expected behaviour. Catching this here
              // because sometimes there needs to be multiple attempts to redirect to properly handle multiple tabs
              if (!isNavigationFailure(err, NavigationFailureType.duplicated)) {
                throw err;
              }
            }
          } else {
            this.$taskNotesHub.stop();
            this.$encountersHub.stop();
            resetTracking();
          }
        },
        immediate: true
      },
      "rootStore.member.termsOfService": {
        async handler(newValue) {
          if (newValue) {
            await this.startConnections();
          }
        },
        immediate: true
      },
      "$route"(to) {
        try {
          if (!this.oursEnabled) {
            const windowLocal = window as any;
            windowLocal.uetq = windowLocal.uetq || [];
            windowLocal.uetq.push("event", "page_view", {
              page_path: to.path
            });
          }
        } catch (e) {
          // If for whatever reason bing fails. Don't block the app from functioning
          console.warn(e);
        }
      },
      "oursPrivacyId": {
        handler() {
          void initOP();
          // safety net: identify user with Ours, if user is already authenticated
          identifyUser(this.rootStore.member);
        },
        immediate: true
      }
    },

    async created() {
      const userManager = await getActiveUserManager();
      const user = await userManager?.getUser();
      if (user && user.expired) {
        console.debug("Login expired");
        await this.rootStore.logout();
      }

      userManager?.events.addAccessTokenExpired(
        this.userTokenExpiredOrUnloaded
      );
      userManager?.events.addUserUnloaded(this.userTokenExpiredOrUnloaded);
      userManager?.events.addUserLoaded(this.userLoadedEvent);

      this.idleTimeoutHandle = setInterval(this.onIdled, 5000);

      document.addEventListener("mousedown", this.onUserActivity);
      document.addEventListener("mousemove", this.onUserActivity);
      document.addEventListener("touchstart", this.onUserActivity);
      document.addEventListener("scroll", this.onUserActivity);
      document.addEventListener("keydown", this.onUserActivity);

      // Hack workaround for iOS insisting on "zooming in" on form inputs but then never resetting it causing issues like double scrollbars, or misaligned content
      // Theoretically setting font-size to 16px on all inputs should work but hasn't seemed to for us
      // Setting maximum scale to 1.0 supposedly disables this "feature" on iOS but also disables zooming on android, so we have to add this to the meta tag dynamically
      const isIOS =
        (!(window as any).MSStream &&
          /iPad|iPhone|iPod/.test(navigator.userAgent)) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

      if (isIOS) {
        const el = document.querySelector("meta[name=viewport]");

        if (el !== null) {
          let content = el.getAttribute("content");
          content = [content, "maximum-scale=1.0"].join(", ");
          el.setAttribute("content", content);
        }
      }

      await this.loadStripe();
    },

    async unmounted() {
      clearInterval(this.idleTimeoutHandle);

      document.removeEventListener("mousedown", this.onUserActivity);
      document.removeEventListener("mousemove", this.onUserActivity);
      document.removeEventListener("touchstart", this.onUserActivity);
      document.removeEventListener("scroll", this.onUserActivity);
      document.removeEventListener("keydown", this.onUserActivity);

      const userManager = await getActiveUserManager();
      userManager?.events.removeAccessTokenExpired(
        this.userTokenExpiredOrUnloaded
      );
      userManager?.events.removeUserUnloaded(this.userTokenExpiredOrUnloaded);
      userManager?.events.removeUserLoaded(this.userLoadedEvent);
    },

    methods: {
      showConsentPreferences() {
        // Click the hidden link in index.html that Termly detected at initialization
        const hiddenLink = document.getElementById(
          "termly-consent-preferences"
        );
        if (hiddenLink) {
          hiddenLink.click();
        }
      },

      async startConnections() {
        this.$taskNotesHub.$on("connected", this.tasksConnected);
        if (!this.$taskNotesHub.connected()) {
          await this.$taskNotesHub.start();
        }
        this.$encountersHub.$on("connected", this.encountersConnected);
        if (!this.$encountersHub.connected()) await this.$encountersHub.start();
      },
      async tasksConnected(connected: boolean) {
        if (connected) {
          await this.$taskNotesHub.watchMyEzra();
        }
      },

      async encountersConnected(connected: boolean) {
        if (connected) {
          await this.$encountersHub.watch();
        }
      },

      async onIdled() {
        // We need to check this against activity time that's in the local storage,
        // this will allow us to prevent logging out from idle in other tabs and keep
        // the idle timer unified across browser tabs.
        if (this.authenticated) {
          const currentTime = new Date().getTime();
          const user = await (await getActiveUserManager())?.getUser();
          if (
            (localStorage.lastActionTime &&
              currentTime - localStorage.lastActionTime >
                Times.InactiveLogoutIntervalMs) ||
            (user && user.expired)
          ) {
            this.rootStore.logout();
            this.showingToast = true;
            this.toastStore.showToast({
              message: logoutMessage,
              duration: Times.ToastNoTimerDuration
            });
          }
        }
      },

      updateLastActionTime: throttle(
        async function () {
          localStorage.setItem(
            "lastActionTime",
            new Date().getTime().toString()
          );
        },
        5000,
        { leading: true }
      ),

      onUserActivity() {
        this.updateLastActionTime();
        if (this.showingToast) {
          this.showingToast = false;
          setTimeout(
            () => this.toastStore.hideToast(logoutMessage),
            Times.LogoutNoticeDurationMs
          );
        }
      },

      async userTokenExpiredOrUnloaded() {
        const userManager = await getActiveUserManager();
        const user = await userManager?.getUser();
        // User expired or unloaded event can sometimes fire late (eg. after a logout/login, check to make sure its actually valid)
        if (user == null || user.expired) {
          await this.rootStore.logout();
        }
      },

      userLoadedEvent(cb: User) {
        let roles: string[] = [];
        if (typeof cb?.profile?.role === "string") {
          roles = [cb?.profile?.role];
        } else if (Array.isArray(cb?.profile?.role)) {
          roles = cb?.profile?.role;
        }
        Sentry.setUser({
          id: cb?.profile?.sub,
          ip_address: "{{auto}}",
          roles: roles.join(",")
        });
      }
    }
  });
</script>

<style lang="scss">
  html,
  body,
  #app {
    height: 100%;

    color: $c--black-chocolate;

    // Disable overscroll, on iOS this causes weirdo viewport issues
    overscroll-behavior-y: none;
  }

  body {
    overflow: hidden;
  }
</style>
